import React, { useEffect, useRef, useState } from 'react';
import {
  FiUsers,
  FiUserPlus,
  FiAward,
  FiRefreshCw,
  FiBriefcase,
  FiFileText,
  FiDollarSign,
  FiHome,
  FiUserCheck,
  FiClock,
  FiAlertCircle,
  FiBarChart2,
  FiGitBranch
} from 'react-icons/fi';
import Chart from 'chart.js/auto';
import './HomePage.css';

const url = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const chartRef = useRef(null);
  const monthlyChartRef = useRef(null);

  // Safe fetch
  const safeFetchJson = async (fetchUrl, headers = {}) => {
    try {
      const res = await fetch(fetchUrl, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      console.warn(fetchUrl, err.message);
      return null;
    }
  };

  // Fetch dashboard stats
  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [
        clientData,
        customerData,
        contractData,
        paymentData,
        transferData,
        jobData
      ] = await Promise.all([
        safeFetchJson(`${url}/api/client/stats`, headers),
        safeFetchJson(`${url}/api/customer/stats`, headers),
        safeFetchJson(`${url}/api/contract/stats`, headers),
        safeFetchJson(`${url}/api/payment/stats`, headers),
        safeFetchJson(`${url}/api/transfer/stats`, headers),
        safeFetchJson(`${url}/api/jobcategory/stats`, headers),
      ]);

      setStats({
        clients: {
          total: clientData?.totalClients ?? 0,
          active: clientData?.activeClients ?? 0,
          inactive: clientData?.inactiveClients ?? 0,
        },
        customers: {
          total: customerData?.totalCustomers ?? 0,
          active: customerData?.activeCustomers ?? 0,
          inactive: customerData?.inactiveCustomers ?? 0,
          newThisMonth: customerData?.newThisMonth ?? 0,
        },
        contracts: {
          total: contractData?.totalContracts ?? 0,
          active: contractData?.activeContracts ?? 0,
          inactive: contractData?.inactiveContracts ?? 0,
          expiringSoon: contractData?.expiringSoon ?? 0,
          expired: contractData?.expired ?? 0,
          newThisMonth: contractData?.newThisMonth ?? 0,
          contractChart: contractData?.contractChart ?? Array(12).fill(0),
        },
        payments: {
          total: paymentData?.totalPayments ?? 0,
          newThisMonth: paymentData?.newThisMonth ?? 0,
        },
        transfers: {
          total: transferData?.totalTransfers ?? 0,
          completed: transferData?.completed ?? 0,
          pending: transferData?.pending ?? 0,
          rejected: transferData?.rejected ?? 0,
          approved: transferData?.approved ?? 0,
        },
        jobs: {
          total: jobData?.totalJobs ?? 0,
          active: jobData?.activeJobs ?? 0,
          inactive: jobData?.inactiveJobs ?? 0,
        },
        memberChart: customerData?.customerChart ?? Array(12).fill(0),
        contractChart: contractData?.contractChart ?? Array(12).fill(0),
        lastUpdated: new Date(),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 300000);
    return () => clearInterval(interval);
  }, []);

  // Charts - Now showing Customers and Contracts
  useEffect(() => {
    if (!chartRef.current || !monthlyChartRef.current) return;

    if (chartRef.current.chart) chartRef.current.chart.destroy();
    if (monthlyChartRef.current.chart) monthlyChartRef.current.chart.destroy();

    // First chart - Line chart for Customers
    chartRef.current.chart = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        datasets: [{
          label: 'Customers Registered',
          data: stats.memberChart || [],
          borderColor: '#10B981',
          backgroundColor: 'rgba(16,185,129,0.15)',
          fill: true,
          tension: 0.4,
        }],
      },
      options: { 
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Customers Registered by Month'
          }
        }
      }
    });

    // Second chart - Bar chart for Customers vs Contracts
    monthlyChartRef.current.chart = new Chart(monthlyChartRef.current, {
      type: 'bar',
      data: {
        labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        datasets: [
          {
            label: 'Customers Registered',
            data: stats.memberChart || [],
            backgroundColor: '#10B981',
          },
          {
            label: 'Contracts Created',
            data: stats.contractChart || [],
            backgroundColor: '#F43F5E',
          },
        ],
      },
      options: { 
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Customers vs Contracts by Month'
          }
        }
      }
    });
  }, [stats]);

  return (
    <div className="home-container">
      {/* HEADER */}
      <header className="home-header">
        <h1 className="welcome-title">
          SOMIS <span className="brand">MANAGEMENT</span>
        </h1>

        <button className="refresh-btn" onClick={fetchStats} disabled={loading}>
          <FiRefreshCw className={loading ? 'spinning' : ''} />
          {loading ? ' Loading...' : ' Refresh'}
        </button>
      </header>

      {error && <div className="error-alert">Error: {error}</div>}

      {/* STATS CARDS */}
      <section className="stats-grid">
        {/* Total Clients */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiHome />
          </div>
          <p className="stat-label">Total Clients</p>
          <h3 className="stat-value">{stats.clients?.total || 0}</h3>
        </div>

        {/* Total Customers */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <p className="stat-label">Total Customers</p>
          <h3 className="stat-value">{stats.customers?.total || 0}</h3>
        </div>

        {/* Total Contracts */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiFileText />
          </div>
          <p className="stat-label">Total Contracts</p>
          <h3 className="stat-value">{stats.contracts?.total || 0}</h3>
        </div>

        {/* Total Transfers */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiGitBranch />
          </div>
          <p className="stat-label">Total Transfers</p>
          <h3 className="stat-value">{stats.transfers?.total || 0}</h3>
        </div>

        {/* Total Jobs */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiBriefcase />
          </div>
          <p className="stat-label">Total Jobs</p>
          <h3 className="stat-value">{stats.jobs?.total || 0}</h3>
        </div>

        {/* New Contracts This Month */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiAward />
          </div>
          <p className="stat-label">New Contracts This Month</p>
          <h3 className="stat-value">{stats.contracts?.newThisMonth || 0}</h3>
        </div>

        {/* Active Clients */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiUserCheck />
          </div>
          <p className="stat-label">Active Clients</p>
          <h3 className="stat-value">{stats.clients?.active || 0}</h3>
        </div>

        {/* Active Customers */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiUserPlus />
          </div>
          <p className="stat-label">Active Customers</p>
          <h3 className="stat-value">{stats.customers?.active || 0}</h3>
        </div>

        {/* Active Contracts */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiBarChart2 />
          </div>
          <p className="stat-label">Active Contracts</p>
          <h3 className="stat-value">{stats.contracts?.active || 0}</h3>
        </div>

        {/* Contracts Expiring Soon */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiClock />
          </div>
          <p className="stat-label">Contracts Expiring Soon</p>
          <h3 className="stat-value">{stats.contracts?.expiringSoon || 0}</h3>
        </div>

        {/* Completed Transfers */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiAward />
          </div>
          <p className="stat-label">Completed Transfers</p>
          <h3 className="stat-value">{stats.transfers?.completed || 0}</h3>
        </div>

        {/* Active Jobs */}
        <div className="stat-card">
          <div className="stat-icon">
            <FiBriefcase />
          </div>
          <p className="stat-label">Active Jobs</p>
          <h3 className="stat-value">{stats.jobs?.active || 0}</h3>
        </div>
      </section>

      {/* CHARTS */}
      <section className="charts-section">
        <div className="chart-container">
          <canvas ref={chartRef}></canvas>
        </div>

        <div className="chart-container">
          <canvas ref={monthlyChartRef}></canvas>
        </div>
      </section>
    </div>
  );
};

export default Home;